/**
 * ОТКАЗ КОМАНДЫ СЦЕНЫ ВИДЕН ЧЕЛОВЕКУ, А НЕ ТОЛЬКО СТОРУ.
 *
 * `SceneCommandStore.fail()` складывал причину в `lastFailure`, и НИ ОДИН компонент её не
 * читал. Живой прогон: «Добавить» в библиотеке → POST .../rooms/R01/add-module → 503
 * `CATALOG_TEMPLATE_MATERIAL_AUTHORITY_MISSING` → в интерфейсе НИЧЕГО. Ни тоста, ни
 * подписи, ни спиннера: кнопка нажата, модуль не появился, причина известна только в
 * сетевой панели. Это касается всех команд очереди — вставки, замены, броска наполнения,
 * съёмки рендера и операций с видами.
 *
 * Владелец живёт в оболочке приложения (смонтирована всегда), показывает причину один раз
 * и помечает её как показанную, чтобы тост не повторялся на каждом рендере.
 */
import { useEffect } from 'react';
import { toast } from 'sonner';
import { useSceneCommandStore } from '@/shared/store/scene-command-store';
import { pushNotification } from '@/shared/store/notifications-store';

/** Заголовок отказа по роду команды: человек должен понять, ЧТО не получилось. */
const FAILURE_TITLES: Record<string, string> = {
  'module.add': 'Модуль не добавлен',
  'module.replace': 'Модуль не заменён',
  'fill.pointer-drop': 'Наполнение не применено',
  'render.capture': 'Рендер не снят',
  'view.snapshot': 'Вид не сохранён',
  'view.restore': 'Вид не восстановлен',
  'view.remove': 'Вид не удалён',
};

export function sceneCommandFailureTitle(kind: string | null | undefined): string {
  return FAILURE_TITLES[String(kind ?? '')] ?? 'Команда сцены не выполнена';
}

const MAX_DESCRIPTION = 240;

/**
 * Причина, пригодная для чтения человеком.
 *
 * Тело отказа бывает длиной в экран (одна и та же нерешённая позиция каталога повторяется
 * по строке на каждую деталь). Тост на весь экран человек закрывает не читая, поэтому
 * машинный код вытаскивается вперёд, а сырой хвост обрезается — полный текст остаётся в
 * сетевой панели, он никуда не девается.
 */
export function sceneCommandFailureDescription(message: string): string {
  const raw = String(message ?? '').trim();
  const code = raw.match(/"code"\s*:\s*"([A-Za-z0-9_.:-]+)"/)?.[1]
    ?? raw.match(/"reasonCode"\s*:\s*"([A-Za-z0-9_.:-]+)"/)?.[1];
  if (code) {
    const reasons = [...raw.matchAll(/"reasonCode"\s*:\s*"([A-Za-z0-9_.:-]+)"/g)]
      .map((match) => match[1]!);
    const distinct = [...new Set(reasons)].filter((reason) => reason !== code);
    return distinct.length ? `${code} · ${distinct.join(', ')}` : code;
  }
  return raw.length > MAX_DESCRIPTION ? `${raw.slice(0, MAX_DESCRIPTION)}…` : raw;
}

export function useSceneCommandFailureNotice(): void {
  const lastFailure = useSceneCommandStore((state) => state.lastFailure);

  useEffect(() => {
    if (!lastFailure) return;
    const description = sceneCommandFailureDescription(lastFailure.message);
    toast.error(sceneCommandFailureTitle(lastFailure.kind), {
      description,
      duration: 12_000,
    });
    /* Тост исчез — отказ остаётся в журнале колокола (VIS-14). */
    pushNotification({
      kind: 'error',
      title: sceneCommandFailureTitle(lastFailure.kind),
      description,
    });
    useSceneCommandStore.getState().acknowledgeFailure(lastFailure.commandId);
  }, [lastFailure]);
}
